// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useEntrega.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { entregaService, type FiltroEntregas } from '../services/entregaService';
import type { ListaFiltros } from '../services/listaFiltros';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type {
  AtualizarStatusEntregaRequest,
  ConferirEntregaProdutoRequest,
  FecharConferenciaEntregaRequest,
} from '../types';

/** Detalhe completo de uma entrega (produtos, recebimentos, totalizadores). */
export const useEntregaDetalhe = (id: number | null) =>
  useQuery({
    queryKey: ['entrega', 'detalhe', id],
    queryFn: () => entregaService.getById(id as number),
    enabled: id != null,
  });

/** Mutations de conferência/separação de uma entrega (escrita). */
export const useEntregaConferencia = (entregaId: number | null) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['entrega', 'detalhe', entregaId] });
    queryClient.invalidateQueries({ queryKey: ['entrega'] });
  };

  const conferirProduto = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: ConferirEntregaProdutoRequest }) =>
      entregaService.conferirProduto(entregaId as number, itemId, data),
    onSuccess: invalidate,
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const fecharConferencia = useMutation({
    mutationFn: (data: FecharConferenciaEntregaRequest = {}) =>
      entregaService.fecharConferencia(entregaId as number, data),
    onSuccess: () => {
      invalidate();
      toast.success('Conferência fechada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const reabrirConferencia = useMutation({
    mutationFn: () => entregaService.reabrirConferencia(entregaId as number),
    onSuccess: () => {
      invalidate();
      toast.success('Conferência reaberta');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    conferirProduto: conferirProduto.mutate,
    isConferindoProduto: conferirProduto.isPending,
    fecharConferencia: fecharConferencia.mutate,
    isFechandoConferencia: fecharConferencia.isPending,
    reabrirConferencia: reabrirConferencia.mutate,
    isReabrindoConferencia: reabrirConferencia.isPending,
  };
};

interface UseEntregaParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, rota, motorista e ordem — aplicados no servidor. */
  filtros?: FiltroEntregas | ListaFiltros;
}

export const useEntrega = ({ pagina = 1, tamanhoPagina = 20, filtros }: UseEntregaParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['entrega', pagina, tamanhoPagina, filtros],
    queryFn: () => entregaService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  // KPIs completos da consulta inteira (total, retiradas, valor pendente) — o status não entra:
  // os cards mostram a divisão por status. O `resumo` da listagem (v118) segue em `resumoLista`.
  const resumo = useQuery({
    queryKey: ['entrega', 'resumo', filtros],
    queryFn: () => entregaService.getResumo(filtros as FiltroEntregas),
    placeholderData: keepPreviousData,
  });

  const entregas     = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumoLista  = paginado?.resumo;

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarStatusEntregaRequest }) =>
      entregaService.atualizarStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entrega'] });
      toast.success('Status da entrega atualizado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    resumo: resumo.data,
    entregas,
    total,
    totalPaginas,
    resumoLista,
    isLoading,
    isFetching,
    atualizarStatus: updateStatusMutation.mutate,
    isUpdating: updateStatusMutation.isPending,
  };
};
